// R18 — WHAT A SEAL POLL ANSWER MEANS, declared once and read by both sides.
//
// SF2 detaches the seal pipeline: the request returns a jobKey immediately and the creator polls
// /api/seals/status until the seal is ready or has failed. The server and the client agreed on the
// four STRINGS and disagreed on what one of them MEANT:
//
//     server/index.js   "...or unknown (expired / never started, treated as a SOFT FAILURE
//                        the member can just re-run)"
//     src/App.tsx       // "working" / "unknown" -> keep waiting
//
// So a job the server had already declared gone kept the rotating status line turning for the full
// five minutes of the client's deadline. A paying member watched a spinner on a job that was over.
//
// THE DISTINCTION THIS MODULE EXISTS TO MAKE, and it is the reason it is not a one-line lookup:
// a status the server DELIBERATELY SENT is an ANSWER and must be acted on, while an ABSENT or
// unparseable payload is TRANSPORT NOISE — one 502, one dropped connection, one JSON parse that
// came back `{}` — and re-polling is the right thing to do with that. Collapsing the two is how
// `unknown` came to mean "keep waiting" in the first place.
//
// Measured before it was written: `inFlight.set(sealGenKey, …)` runs synchronously BEFORE the
// `queued` response is sent, so by the time the client holds a jobKey the job is registered. There
// is no start-up race, and an `unknown` after that is genuinely terminal — a redeploy, the registry
// TTL, or an expired session.

export const SEAL_WORKING = "working";
export const SEAL_READY = "ready";
export const SEAL_FAILED = "failed";
// The registry has no record of this job. Expired, never started, or the session behind it is gone.
export const SEAL_UNKNOWN = "unknown";

// Every status the server is allowed to send. A poll answer outside this set is not an answer.
export const SEAL_STATUSES = [SEAL_WORKING, SEAL_READY, SEAL_FAILED, SEAL_UNKNOWN];

// The statuses that END the poll loop. `unknown` is here on the server's own declaration.
export const SEAL_TERMINAL_STATUSES = [SEAL_READY, SEAL_FAILED, SEAL_UNKNOWN];

// SF3's sentence: honest, one message, one tap to run it again, and never "could not generate"
// (that is a vendor error and there is no vendor on this path any more).
export const SEAL_LOST_MESSAGE = "Your seal could not be completed this time. Nothing was lost: your details are saved, and one tap runs it fresh.";

export function isSealStatus(status) {
  return SEAL_STATUSES.includes(String(status || "").trim());
}

// THE ONE QUESTION THE POLL LOOP ASKS: "ready" | "failed" | "wait".
export function sealPollOutcome(payload) {
  const status = String(payload?.status || "").trim();
  // Not an answer at all — a dropped poll, a gateway hiccup, a body that would not parse. Ask again.
  if (!isSealStatus(status)) return "wait";
  if (status === SEAL_WORKING) return "wait";
  // A success with no picture in it is not a success. It used to fall through to the waiting
  // branch, which turned a malformed payload into five minutes of nothing.
  if (status === SEAL_READY) return payload?.imageUrl ? "ready" : "failed";
  return "failed"; // failed, and unknown — the server's own reading of it
}

// What she is told when the loop ends badly. The server's own sentence when it sent one (the
// expired-session message was reaching nobody, because the loop never read a poll's message), and
// SF3's honest fallback otherwise.
// 14-B7c: the fallback is a PARAMETER with the English sentence as its default, not a required
// argument. This module imports nothing (r5’s strip-loader entry requirement) so it cannot read the
// strings catalog, and R18’s own fixture calls this with one argument and reads the English back — a
// required parameter would have broken that rather than found anything. The client passes her row.
export function sealFailureMessage(payload, fallback = SEAL_LOST_MESSAGE) {
  const message = String(payload?.message || "").trim();
  return message || String(fallback || SEAL_LOST_MESSAGE);
}

// Whether the failure is one a re-tap can fix. A lost job always is: her details are still on the
// form and nothing was consumed that a fresh run cannot redo.
export function sealFailureRetryable(payload) {
  const status = String(payload?.status || "").trim();
  if (status === SEAL_UNKNOWN) return true;
  if (status === SEAL_READY) return true; // ready-with-no-image: a rerun is exactly the right move
  return payload?.retryable === true || payload?.reworking === true || payload?.failed === true;
}
